import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import {
  SectionTeacherProjectTeachingTime,
  SectionTeacherRow,
  TeacherWorkloadMode,
} from 'src/app/services/models/subjects/subject.model';
import {
  parseTeachingRangeValue,
  teachingTimesValidator,
} from '../domain/subject-management-create.utils';

export interface SectionStateMaps {
  teacherRowsBySection: Record<number, SectionTeacherRow[]>;
  teacherWorkloadModeBySection: Record<number, TeacherWorkloadMode>;
  teacherSectionLockedBySection: Partial<Record<number, boolean>>;
  submittedSectionStatusBySection: Partial<Record<number, string>>;
  sectionExpandedBySection?: Partial<Record<number, boolean>>;
}

export function createSectionForm(
  fb: FormBuilder,
  isProjectResearch: boolean,
  digitsOnlyValidators: ValidatorFn[],
  options: {
    isLocked?: boolean;
    sectionValue?: string;
    validateWorkloadHours?: boolean;
    workloadHours?: string;
  } = {}
): FormGroup {
  const sectionRequiredValidator = isProjectResearch ? [] : [Validators.required];
  const numericSectionValidator = isProjectResearch ? [] : digitsOnlyValidators;
  const workloadHoursValidator = isProjectResearch || options.validateWorkloadHours === false
    ? []
    : [Validators.required, Validators.pattern(/^\d+(?:\.\d+)?$/)];
  const teachingTimesValidators = isProjectResearch ? [] : [teachingTimesValidator()];

  return fb.group({
    section: [{ value: options.sectionValue ?? '', disabled: options.isLocked === true }, digitsOnlyValidators],
    teachingType: [null, sectionRequiredValidator],
    room: [''],
    studentCount: ['', numericSectionValidator],
    workloadHours: [options.workloadHours ?? '', workloadHoursValidator],
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    teachingTimes: fb.array<FormGroup>([createTeachingTimeForm(fb, isProjectResearch)], {
      validators: teachingTimesValidators,
    }),
  });
}

export function createTeachingTimeForm(fb: FormBuilder, isProjectResearch: boolean): FormGroup {
  const validators = isProjectResearch ? [] : [Validators.required];

  return fb.group({
    teachingDay: [null, validators],
    startTime: [null, validators],
    endTime: [null, validators],
  });
}

export function createTeacherDraftForm(fb: FormBuilder, row?: SectionTeacherRow): FormGroup {
  return fb.group({
    userId: [row?.userId ?? null],
    fullName: [row?.fullName ?? ''],
    department: [row?.department ?? '-'],
    teachingRange: [parseTeachingRangeValue(row?.teachingRange)],
  });
}

export function createProjectTeachingTimeForm(
  fb: FormBuilder,
  time?: SectionTeacherProjectTeachingTime
): FormGroup {
  return fb.group({
    teachingDay: [time?.teachingDay ?? null, Validators.required],
    startTime: [time?.startTime ?? null, Validators.required],
    endTime: [time?.endTime ?? null, Validators.required],
  });
}

function getSectionDuplicateKey(section: FormGroup): string | null {
  const sectionValue = String(section.get('section')?.value ?? '').trim();
  const teachingTypeValue = String(section.get('teachingType')?.value ?? '').trim();

  if (sectionValue === '' || teachingTypeValue === '') {
    return null;
  }

  return `${sectionValue}::${teachingTypeValue}`;
}

export function updateSectionDuplicateErrors(sectionForms: FormGroup[]): void {
  const sectionEntries = sectionForms
    .map((section) => ({
      control: section.get('section'),
      duplicateKey: getSectionDuplicateKey(section),
    }))
    .filter((entry): entry is { control: AbstractControl; duplicateKey: string | null } => entry.control != null);
  const sectionCounts = new Map<string, number>();

  sectionEntries.forEach(({ duplicateKey }) => {
    if (duplicateKey != null) {
      sectionCounts.set(duplicateKey, (sectionCounts.get(duplicateKey) ?? 0) + 1);
    }
  });

  sectionEntries.forEach(({ control, duplicateKey }) => {
    const errors = { ...(control.errors ?? {}) };

    delete errors['duplicateSection'];

    if (duplicateKey != null && (sectionCounts.get(duplicateKey) ?? 0) > 1) {
      errors['duplicateSection'] = true;
    }

    control.setErrors(Object.keys(errors).length > 0 ? errors : null, { emitEvent: false });
  });
}

export function reindexSectionState(state: SectionStateMaps, removedIndex: number): SectionStateMaps {
  return {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    teacherRowsBySection: reindexRecord(state.teacherRowsBySection, removedIndex) as Record<number, SectionTeacherRow[]>,
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    teacherWorkloadModeBySection: reindexRecord(
      state.teacherWorkloadModeBySection,
      removedIndex
    ) as Record<number, TeacherWorkloadMode>,
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    teacherSectionLockedBySection: reindexRecord(state.teacherSectionLockedBySection, removedIndex),
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    submittedSectionStatusBySection: reindexRecord(state.submittedSectionStatusBySection, removedIndex),
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    sectionExpandedBySection: reindexRecord(state.sectionExpandedBySection ?? {}, removedIndex),
  };
}

function reindexRecord<T>(
  record: Partial<Record<number, T>>,
  removedIndex: number
): Partial<Record<number, T>> {
  const result: Partial<Record<number, T>> = {};

  Object.entries(record).forEach(([indexText, value]) => {
    const index = Number(indexText);

    if (!Number.isInteger(index) || index === removedIndex || value == null) {
      return;
    }

    result[index > removedIndex ? index - 1 : index] = value;
  });

  return result;
}
